import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import MobileFriendlyDatePicker from '../components/MobileFriendlyDatePicker';
import VoiceRecorder from '../components/VoiceRecorder';
import { Trash2, Plus, Camera, X, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReflectionLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [pupils, setPupils] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [selectedView, setSelectedView] = useState('all');
  const [formData, setFormData] = useState({
    pupil_id: '',
    lesson_date: '',
    lesson_hours: 1.0,
    lesson_time: '',
    answers: [],
    skills: '',
    notes: '',
    send_email: false
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [hasVoiceNote, setHasVoiceNote] = useState(false);
  const [lessonSuggestion, setLessonSuggestion] = useState('');
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);

  useEffect(() => {
    fetchReflectionLogs();
    fetchPupils();
    fetchQuestions();
  }, []);

  const fetchReflectionLogs = async () => {
    try {
      const response = await axios.get(`${API}/reflection-logs`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching reflection logs:', error);
      toast.error('Failed to load reflection logs');
    }
  };

  const fetchPupils = async () => {
    try {
      const response = await axios.get(`${API}/pupils`);
      setPupils(response.data);
    } catch (error) {
      console.error('Error fetching pupils:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questions`);
      setQuestions(response.data);
      // Initialize answers array
      const initialAnswers = response.data.map(q => ({
        question_id: q.id,
        answer_text: ''
      }));
      setFormData(prev => ({ ...prev, answers: initialAnswers }));
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setFormData(prev => ({
      ...prev,
      answers: prev.answers.map(a => 
        a.question_id === questionId 
          ? { ...a, answer_text: answer }
          : a
      )
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setIsUploading(true);
    const newUploads = [];
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.filename || file.name} is not an image`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        continue;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await axios.post(`${API}/attachments/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newUploads.push({
          id: response.data.id,
          filename: response.data.filename,
          size: response.data.size,
          previewUrl: URL.createObjectURL(file)
        });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploadedFiles(prev => [...prev, ...newUploads]);
    setIsUploading(false);
    e.target.value = '';
  };

  const removeUploadedFile = (fileId) => {
    setUploadedFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pupil_id || !formData.lesson_date) {
      toast.error('Please select a pupil and lesson date');
      return;
    }

    // Filter out empty answers
    const validAnswers = formData.answers.filter(a => a.answer_text.trim() !== '');
    
    if (validAnswers.length === 0) {
      toast.error('Please provide at least one answer');
      return;
    }

    try {
      const submitData = {
        ...formData,
        answers: validAnswers,
        skills: formData.skills.trim() ? [formData.skills.trim()] : [],
        lesson_date: new Date(formData.lesson_date).toISOString(),
        lesson_time: formData.lesson_time || null,
        send_email: formData.send_email,
        attachments: uploadedFiles.length > 0 ? uploadedFiles.map(f => f.id) : null
      };

      await axios.post(`${API}/reflection-logs`, submitData);
      
      const selectedPupil = pupils.find(p => p.id === formData.pupil_id);
      
      // Clean up preview URLs
      uploadedFiles.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
      setUploadedFiles([]);
      
      // Reset form
      setFormData({
        pupil_id: '',
        lesson_date: '',
        lesson_hours: 1.0,
        lesson_time: '',
        answers: questions.map(q => ({ question_id: q.id, answer_text: '' })),
        skills: '',
        notes: '',
        send_email: false
      });
      setIsDialogOpen(false);
      setHasVoiceNote(false);
      setLessonSuggestion('');
      fetchReflectionLogs();
      
      // Show appropriate success message based on email option
      if (formData.send_email && selectedPupil?.email) {
        toast.success(`Reflection log created and emailed to ${selectedPupil.name}! 📧`);
      } else if (formData.send_email && !selectedPupil?.email) {
        toast.success('Reflection log created!');
        toast.info('💡 Add an email to the pupil\'s profile to enable email notifications');
      } else {
        toast.success('Reflection log saved for review! Use "Resend" button to email the pupil.');
      }
    } catch (error) {
      console.error('Error creating reflection log:', error);
      toast.error('Failed to create reflection log');
    }
  };

  const handleDeleteLog = async (logId, pupilName, lessonDate) => {
    if (!window.confirm(`Are you sure you want to delete the reflection log for ${pupilName} from ${new Date(lessonDate).toLocaleDateString()}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/reflection-logs/${logId}`);
      toast.success('Reflection log deleted successfully');
      fetchReflectionLogs(); // Refresh the list
    } catch (error) {
      console.error('Error deleting reflection log:', error);
      toast.error('Failed to delete reflection log');
    }
  };

  const handleGenerateAISummary = async () => {
    // Build answers array with question text for all filled-in answers
    const answersWithQuestions = formData.answers
      .filter(ans => ans.answer_text && ans.answer_text.trim())
      .map(ans => {
        const question = questions.find(q => q.id === ans.question_id);
        return {
          question_text: question ? question.text : 'Unknown question',
          answer_text: ans.answer_text
        };
      });
    
    // Validation: ensure we have at least one answer
    if (answersWithQuestions.length === 0) {
      toast.error('Please answer at least one question before generating AI summary');
      return;
    }

    // Get selected pupil name
    const selectedPupil = pupils.find(p => p.id === formData.pupil_id);
    if (!selectedPupil) {
      toast.error('Please select a pupil first');
      return;
    }

    setIsGeneratingAI(true);
    
    try {
      const summaryRequest = {
        answers: answersWithQuestions,
        pupil_name: selectedPupil.name,
        lesson_hours: formData.lesson_hours || null,
        skills: formData.skills && formData.skills.trim() ? [formData.skills.trim()] : null
      };

      const response = await axios.post(`${API}/reflection-logs/generate-ai-summary`, summaryRequest);
      
      // Update the notes field with AI-generated summary
      setFormData({
        ...formData,
        notes: response.data.summary
      });
      
      toast.success('AI summary generated successfully!');
    } catch (error) {
      console.error('Error generating AI summary:', error);
      const errDetail = error.response?.data?.detail;
      const errMsg = typeof errDetail === 'string' ? errDetail : (Array.isArray(errDetail) ? errDetail.map(e => e.msg || e).join(', ') : error.message);
      toast.error('Failed to generate AI summary: ' + errMsg);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const getPupilName = (pupilId) => {
    const pupil = pupils.find(p => p.id === pupilId);
    return pupil ? pupil.name : 'Unknown';
  };

  const getQuestionText = (questionId, answer) => {
    if (answer?.question_text) return answer.question_text;
    const question = questions.find(q => q.id === questionId);
    return question ? question.text : 'Unknown Question';
  };

  const getQuestion = (questionId) => {
    return questions.find(q => q.id === questionId);
  };

  const renderAnswerDisplay = (answer) => {
    const question = getQuestion(answer.question_id);
    
    if (!question) {
      return <p className="text-gray-600">{answer.answer_text}</p>;
    }

    if (question.question_type === 'scale') {
      const scaleValue = parseInt(answer.answer_text);
      const scaleRange = question.scale_max - question.scale_min + 1;
      const percentage = ((scaleValue - question.scale_min) / (question.scale_max - question.scale_min)) * 100;
      
      return (
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <span className="text-2xl font-bold text-blue-600">{scaleValue}</span>
            <span className="text-sm text-gray-600">out of {question.scale_max}</span>
            <Badge variant={scaleValue >= question.scale_max * 0.8 ? 'default' : scaleValue >= question.scale_max * 0.6 ? 'secondary' : 'destructive'}>
              {scaleValue >= question.scale_max * 0.8 ? 'Excellent' : scaleValue >= question.scale_max * 0.6 ? 'Good' : 'Needs Improvement'}
            </Badge>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>
      );
    }

    return <p className="text-gray-600">{answer.answer_text}</p>;
  };

  // Group logs by pupil
  const groupedLogs = logs.reduce((acc, log) => {
    const pupilName = getPupilName(log.pupil_id);
    if (!acc[pupilName]) {
      acc[pupilName] = [];
    }
    acc[pupilName].push(log);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Reflection Logs</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">View and manage all reflection logs across all pupils</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-new-log-btn" disabled={pupils.length === 0 || questions.length === 0}>
              <Plus className="mr-2" size={16} />
              Create New Log
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Reflection Log</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Voice Note - prominent at the top */}
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-indigo-800">Voice Note (Optional)</h3>
                    <p className="text-xs text-indigo-600 mt-0.5">Record your lesson observations — they'll be transcribed into the notes field</p>
                  </div>
                  <VoiceRecorder onTranscription={(text) => {
                    setFormData(prev => ({
                      ...prev,
                      notes: prev.notes ? prev.notes + '\n\n' + text : text
                    }));
                    setHasVoiceNote(true);
                    setLessonSuggestion('');
                  }} />
                </div>

                {/* AI Suggest Next Lesson - appears after voice transcription */}
                {hasVoiceNote && !lessonSuggestion && (
                  <div className="flex items-center gap-3 pt-2 border-t border-indigo-200">
                    <Button
                      type="button"
                      size="sm"
                      disabled={isGeneratingSuggestion || !formData.notes.trim()}
                      data-testid="suggest-lesson-btn"
                      onClick={async () => {
                        setIsGeneratingSuggestion(true);
                        try {
                          const response = await axios.post(`${API}/voice/suggest-lesson`, {
                            notes: formData.notes,
                            pupil_id: formData.pupil_id || null
                          });
                          setLessonSuggestion(response.data.suggestion);
                        } catch (error) {
                          const msg = error.response?.data?.detail || error.message;
                          toast.error('Failed to generate suggestion: ' + msg);
                        } finally {
                          setIsGeneratingSuggestion(false);
                        }
                      }}
                      className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                    >
                      {isGeneratingSuggestion ? (
                        <><Loader2 size={14} className="mr-1.5 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles size={14} className="mr-1.5" /> AI Suggest Next Lesson</>
                      )}
                    </Button>
                    <span className="text-xs text-indigo-500">Get an AI-suggested plan for the next lesson based on your notes</span>
                  </div>
                )}

                {/* Suggestion Preview with Save/Discard */}
                {lessonSuggestion && (
                  <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid="lesson-suggestion-preview">
                    <div className="bg-emerald-50 px-3 py-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5">
                        <Sparkles size={14} /> Suggested Next Lesson Plan
                      </span>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          data-testid="save-suggestion-btn"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              notes: prev.notes + '\n\n--- AI Suggested Next Lesson ---\n' + lessonSuggestion
                            }));
                            setLessonSuggestion('');
                            toast.success('Lesson suggestion saved to notes');
                          }}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                        >
                          Save to Log
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          data-testid="discard-suggestion-btn"
                          onClick={() => {
                            setLessonSuggestion('');
                            toast.info('Suggestion discarded');
                          }}
                          className="h-7 text-xs border-gray-300"
                        >
                          Discard
                        </Button>
                      </div>
                    </div>
                    <div className="p-3 text-sm text-gray-700 whitespace-pre-wrap bg-white">{lessonSuggestion}</div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="pupil">Pupil *</Label>
                  <select
                    id="pupil"
                    value={formData.pupil_id}
                    onChange={(e) => setFormData({...formData, pupil_id: e.target.value})}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select a pupil</option>
                    {pupils.map(pupil => (
                      <option key={pupil.id} value={pupil.id}>{pupil.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="lesson_date">Lesson Date *</Label>
                  <Input
                    id="lesson_date"
                    type="date"
                    value={formData.lesson_date}
                    onChange={(e) => setFormData({...formData, lesson_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lesson_time">Lesson Time</Label>
                  <Input
                    id="lesson_time"
                    type="time"
                    data-testid="lesson-time-input"
                    value={formData.lesson_time}
                    onChange={(e) => setFormData({...formData, lesson_time: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="lesson_hours">Hours *</Label>
                  <Input
                    id="lesson_hours"
                    type="number"
                    step="0.5"
                    min="0.5"
                    max="8"
                    value={formData.lesson_hours}
                    onChange={(e) => setFormData({...formData, lesson_hours: parseFloat(e.target.value)})}
                    placeholder="1.0"
                    required
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-700">Skills & Goals</h3>
                <p className="text-sm text-slate-600">What skills and goals did you agree to work on this lesson?</p>
                <Textarea
                  value={formData.skills}
                  onChange={(e) => setFormData({...formData, skills: e.target.value})}
                  placeholder="e.g. Roundabouts - approach speed and lane positioning, Mirror checks before signalling, Parallel parking on the left..."
                  rows={3}
                  data-testid="skills-text-input"
                />
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-700">Reflection Questions</h3>
                {questions.map((question, index) => (
                  <div key={question.id} className="space-y-3 p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">
                        Q{index + 1}: {question.text}
                      </Label>
                      <Badge variant="outline" className={question.question_type === 'scale' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-700'}>
                        {question.question_type === 'scale' ? `Scale ${question.scale_min}-${question.scale_max}` : 'Text'}
                      </Badge>
                    </div>
                    
                    {question.question_type === 'scale' ? (
                      <div className="space-y-3">
                        <div className="px-3">
                          <Slider
                            value={[parseInt(formData.answers.find(a => a.question_id === question.id)?.answer_text || question.scale_min)]}
                            onValueChange={(value) => handleAnswerChange(question.id, value[0].toString())}
                            min={question.scale_min}
                            max={question.scale_max}
                            step={1}
                            className="w-full"
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 px-3">
                          <span>{question.scale_min}</span>
                          <span className="font-semibold text-gray-700">
                            Current: {formData.answers.find(a => a.question_id === question.id)?.answer_text || question.scale_min}
                          </span>
                          <span>{question.scale_max}</span>
                        </div>
                        <div className="grid grid-cols-10 gap-1 px-3">
                          {Array.from({length: question.scale_max - question.scale_min + 1}, (_, i) => question.scale_min + i).map(num => (
                            <button
                              key={num}
                              type="button"
                              onClick={() => handleAnswerChange(question.id, num.toString())}
                              className={`h-8 text-xs rounded ${
                                parseInt(formData.answers.find(a => a.question_id === question.id)?.answer_text || question.scale_min) === num
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              {num}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Textarea
                        value={formData.answers.find(a => a.question_id === question.id)?.answer_text || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder="Enter your reflection..."
                        rows={3}
                      />
                    )}
                  </div>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="notes">Instructor Notes</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateAISummary}
                    disabled={isGeneratingAI}
                    className="text-xs"
                  >
                    {isGeneratingAI ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        🤖 Generate AI Summary
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="AI can generate a summary based on the pupil's answers, or add your own notes..."
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Answer the reflection questions first, then click "Generate AI Summary" for an automated instructor summary
                </p>
              </div>

              {/* Photo Attachments */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-lg font-semibold text-slate-700">Photos / Screenshots</Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="photo-upload-input"
                    />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-md text-sm font-medium text-slate-700 transition-colors">
                      <Camera size={16} />
                      {isUploading ? 'Uploading...' : 'Add Photos'}
                    </span>
                  </label>
                </div>
                {uploadedFiles.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="relative group rounded-lg overflow-hidden border border-gray-200">
                        <img
                          src={file.previewUrl}
                          alt={file.filename}
                          className="w-full h-28 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeUploadedFile(file.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                        <p className="text-xs text-gray-500 truncate px-2 py-1">{file.filename}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Email Options */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="send_email"
                    checked={formData.send_email}
                    onCheckedChange={(checked) => setFormData({...formData, send_email: checked})}
                  />
                  <div>
                    <Label htmlFor="send_email" className="font-medium cursor-pointer">
                      Send email to pupil now
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-green-600">
                      {formData.send_email 
                        ? "Email will be sent immediately after creating the log" 
                        : "Log will be saved - you can review and send later using the 'Resend' button"}
                    </p>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full">Create Reflection Log</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {pupils.length === 0 || questions.length === 0 ? (
        <Card className="text-center p-8">
          <CardContent>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Setup Required</h3>
            <p className="text-gray-500 mb-4">
              You need to add pupils and questions before creating reflection logs
            </p>
            <div className="flex justify-center space-x-4">
              {pupils.length === 0 && (
                <Link to="/pupils">
                  <Button variant="outline">Add Pupils</Button>
                </Link>
              )}
              {questions.length === 0 && (
                <Link to="/questions">
                  <Button variant="outline">Add Questions</Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div>
          <Tabs defaultValue="grouped" className="mb-6">
            <TabsList>
              <TabsTrigger value="grouped">By Pupil</TabsTrigger>
              <TabsTrigger value="chronological">All Logs</TabsTrigger>
            </TabsList>
            
            <TabsContent value="grouped" className="space-y-6">
              {Object.entries(groupedLogs).map(([pupilName, pupilLogs]) => (
                <Card key={pupilName} className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-xl text-slate-800 flex items-center justify-between">
                      <span>{pupilName}</span>
                      <Badge variant="secondary">{pupilLogs.length} logs</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {pupilLogs.slice(0, 3).map((log) => (
                        <div key={log.id} className="border-l-4 border-blue-200 pl-4 py-2">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-700 flex items-center space-x-2">
                              <span>{new Date(log.lesson_date).toLocaleDateString()}</span>
                              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                                {log.lesson_hours || 1.0}h
                              </Badge>
                            </h4>
                            <span className="text-xs text-gray-500">
                              {log.answers.length} answers
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {log.answers.slice(0, 1).map((answer) => (
                              <p key={answer.question_id} className="truncate">
                                <strong>{getQuestionText(answer.question_id, answer)}:</strong> {answer.answer_text.substring(0, 100)}...
                              </p>
                            ))}
                            {log.skills && log.skills.length > 0 && (
                              <div className="mt-2">
                                <span className="text-xs text-gray-500">Skills: </span>
                                <span className="text-xs text-blue-600">
                                  {log.skills.slice(0, 3).join(', ')}
                                  {log.skills.length > 3 && ` +${log.skills.length - 3} more`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {pupilLogs.length > 3 && (
                        <div className="text-center pt-2">
                          <Link to={`/pupil/${pupilLogs[0].pupil_id}/logs`}>
                            <Button variant="outline" size="sm">
                              View All {pupilLogs.length} Logs for {pupilName}
                            </Button>
                          </Link>
                        </div>
                      )}
                      {pupilLogs.length <= 3 && pupilLogs.length > 0 && (
                        <div className="text-center pt-2">
                          <Link to={`/pupil/${pupilLogs[0].pupil_id}/logs`}>
                            <Button variant="outline" size="sm">
                              View Detailed Logs for {pupilName}
                            </Button>
                          </Link>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="chronological" className="space-y-6">
              {logs.map((log) => (
                <Card key={log.id} className="card-hover">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl text-slate-800 flex items-center space-x-3">
                          <span>{getPupilName(log.pupil_id)}</span>
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                            {log.lesson_hours || 1.0}h
                          </Badge>
                        </CardTitle>
                        <p className="text-sm text-gray-600 mt-1">
                          Lesson Date: {new Date(log.lesson_date).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created: {new Date(log.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="secondary">{log.answers.length} Answers</Badge>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteLog(log.id, getPupilName(log.pupil_id), log.lesson_date)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {log.answers.map((answer, index) => (
                        <div key={answer.question_id} className="border-l-4 border-blue-200 pl-4">
                          <h4 className="font-medium text-gray-700 mb-2">
                            Q{index + 1}: {getQuestionText(answer.question_id, answer)}
                          </h4>
                          {renderAnswerDisplay(answer)}
                        </div>
                      ))}
                      
                      {log.skills && log.skills.length > 0 && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-md">
                          <h4 className="font-medium text-gray-700 mb-2">Skills Practiced</h4>
                          <div className="flex flex-wrap gap-1">
                            {log.skills.map((skill) => (
                              <Badge key={skill} variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {log.notes && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-md">
                          <h4 className="font-medium text-gray-700 mb-1">Additional Notes</h4>
                          <p className="text-gray-600">{log.notes}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          </Tabs>

          {logs.length === 0 && (
            <Card className="text-center p-8">
              <CardContent>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No reflection logs yet</h3>
                <p className="text-gray-500">Create your first reflection log to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};


export default ReflectionLogsPage;
